/**
 * The two duel modes, and the one that does not exist yet.
 *
 * ## Why `gold_pot` is named here at all
 *
 * A mode nobody declared is a mode nobody can refuse. If the domain knew only
 * `no_reward`, an unknown string would fall through whatever `default` branch
 * happened to be nearest - and the nearest default in a duel service is "run
 * the match", which is exactly the outcome a wagered pot must never reach by
 * accident. So the pot is declared, typed and REFUSED, by name, before a room
 * exists.
 *
 * When it does arrive it will move internal **Gold** and nothing else: never
 * Cash, never Pix, never money, never items, never spectators. That sentence is
 * the whole scope of the future feature and it belongs beside the constant it
 * describes, not in a commit message somebody has to go looking for.
 *
 * Nothing in this module moves a balance. There is no escrow, no fee, no payout
 * and no ledger key - by design, and until the prompt that builds them.
 */

/** Every mode the domain can name. Order is not significance. */
export const PVP_MODES = ['no_reward', 'gold_pot'] as const;

export type PvpMode = (typeof PVP_MODES)[number];

/**
 * The modes a room may actually run today.
 *
 * `no_reward` and only `no_reward`. This is a separate list from
 * {@link PVP_MODES} on purpose: "a mode we can parse" and "a mode we can play"
 * are different questions, and collapsing them is how an unfinished feature
 * ships switched on.
 */
export const PVP_PLAYABLE_MODES = ['no_reward'] as const;

export type PvpPlayableMode = (typeof PVP_PLAYABLE_MODES)[number];

export function isPvpMode(value: unknown): value is PvpMode {
  return typeof value === 'string' && (PVP_MODES as readonly string[]).includes(value);
}

/**
 * The stable reason a wagered duel is refused.
 *
 * A MACHINE reason, like every other refusal in this API: the client keys on it
 * and renders its own wording. It says the pot is unavailable; it deliberately
 * does not say "coming soon", "not yet configured" or anything else that would
 * describe the server's configuration to somebody probing it.
 */
export const PVP_GOLD_POT_UNAVAILABLE_REASON = 'pvp_gold_pot_unavailable';

/** The stable reason the whole feature is closed. */
export const PVP_DISABLED_REASON = 'pvp_disabled';

/**
 * Whether a mode may open a room, given what the deployment has switched on.
 *
 * Both arguments are REQUIRED and neither has a default. A default of `true`
 * anywhere on this path would mean a caller that forgot to pass the flag opens
 * a wagered duel, and the entire point of this function is that forgetting is
 * not one of the outcomes.
 *
 * `goldPotEnabled` is `false` when the variable is absent, false, malformed, or
 * anything other than the exact string the configuration accepts - the decision
 * is made in `apps/api/src/pvp/config.ts` and arrives here already reduced to a
 * boolean, so this module stays free of environment parsing.
 */
export function pvpModeRefusal(
  mode: PvpMode,
  flags: { readonly pvpEnabled: boolean; readonly goldPotEnabled: boolean },
): typeof PVP_DISABLED_REASON | typeof PVP_GOLD_POT_UNAVAILABLE_REASON | null {
  if (!flags.pvpEnabled) {
    return PVP_DISABLED_REASON;
  }
  if (mode === 'gold_pot') {
    /*
     * Refused even when the flag is on.
     *
     * There is no escrow, no pot and no payout in this build, so a `true` here
     * would open a room that promises Gold it has no code to move. The flag is
     * necessary and not sufficient: the mode stays refused until the escrow
     * exists, and this line is what the prompt that builds it will delete.
     */
    return PVP_GOLD_POT_UNAVAILABLE_REASON;
  }
  return null;
}

/**
 * The rules version a match was played under.
 *
 * Frozen onto the row at creation. A duel is not an economy, so this settles
 * nothing - but a result recorded under one set of numbers and read back under
 * another is a result nobody can interpret, and the column costs nothing now
 * and cannot be added retroactively to matches already played.
 *
 * ## Version 2: the Mirage rune was retired
 *
 * A pad now grows one of THREE things instead of one of four, so every match
 * played under version 1 was played under a different distribution - which is
 * exactly the kind of difference this number exists to record. Nothing else was
 * touched: the surviving runes keep their durations and their multipliers, and
 * damage, movement, cadence and the end conditions are unchanged.
 *
 * This is a RULES bump and not a protocol one. The wire still carries the
 * (now permanently empty) illusion fields, so `PVP_TEAM_PROTOCOL_VERSION` stays
 * where it is until they are actually removed.
 *
 * ## Version 3: every hull states its own four numbers
 *
 * A welcome now carries a `PvpCombatProfile` per participant - base damage,
 * maximum health, speed and cadence - instead of a single normalised
 * `maxHealth` for the room. Every profile is the shipped default today, so no
 * match plays differently; what changed is that a duel is no longer OBLIGED to
 * be symmetric, which is the property a jewel needs.
 *
 * Both a rules bump and a protocol one, and the two say different things. This
 * number is what `PvpConnection` and `PvpTeamConnection` compare on every
 * welcome, failing with `rules_version_mismatch` rather than drawing a fight
 * they cannot interpret - a client built for version 2 would read a health bar
 * against a denominator that is no longer in the frame.
 * `PVP_TEAM_PROTOCOL_VERSION` moves with it because the team frame's own `v` is
 * a `z.literal`, so a version-3 document is refused by the schema itself.
 *
 * ## Version 4: a socketed jewel changes what a shot does
 *
 * The Chama da Destruição adds `30 bps x levelSum` to the ordinary shot (gate
 * V28G.B). This is the first time a duel's outcome depends on something a
 * player equipped, and it is a RULES bump for a precise reason: the client
 * PREDICTS its own damage from the profile the room states, so a client built
 * for version 3 would read the same profile and compute a smaller shot - and
 * diverge from the authority on every hit rather than once.
 *
 * A duel between two hulls with no jewels plays out identically, to the milli,
 * and `duel-invariance.test.ts` still holds its golden digests. The bump is
 * about what the two ENDS agree the numbers mean, not about what today's
 * matches do.
 *
 * NOT a protocol bump: the frame's shape did not change. The profile has
 * carried `jewels` since gate V28G.A, and a version-3 frame and a version-4
 * frame are byte-identical documents - what moved is the arithmetic behind one
 * of its fields.
 *
 * ## 4 → 5: o Pulso do Dragão (gate V28G.D)
 *
 * `jewel_attack_speed` encurta o intervalo entre salvas por
 * `divTrunc(base x 10000, 10000 + 30 x levelSum)`. A recarga deixa de ser o
 * campo do perfil lido tal como está e passa a ser esse campo COMPOSTO com as
 * joias, o que muda quando cada salva acontece.
 *
 * É um bump de REGRAS pela mesma razão que o anterior, e por uma diferente. A
 * mesma: as duas pontas têm de concordar no que os números do perfil querem
 * dizer. A diferente: aqui o cliente não prediz nada - `PvpPrediction` tem
 * "grant a shot" na lista do que lhe é proibido, e a autoridade continua a ser
 * o único sítio que concede um tiro. O que uma ponta desatualizada faria não é
 * divergir a simular; é DESENHAR uma cadência que nunca combinaria com os
 * eventos que recebe.
 *
 * Continua a não ser um bump de protocolo, de bilhete, de snapshot nem de
 * `BALANCE_VERSION`. `fireIntervalMilliTicks` já viajava e continua a viajar com
 * o mesmo valor - 4 000 - porque o perfil guarda a cadência do CASCO e as joias
 * compõem-se na recarga. Um duelo sem esta joia é byte a byte e tique a tique o
 * duelo de sempre.
 *
 * ## 5 → 6: o Ciclone de Lâminas (gate V28G.F)
 *
 * Um disparo passa a poder carregar um PROC, sorteado quando o projétil nasce,
 * e um acerto com proc entrega dano adicional além do tiro. É a primeira vez
 * que o resultado de um duelo depende de um acontecimento e não só de números.
 *
 * É um bump de REGRAS porque as duas pontas têm de concordar no que um projétil
 * significa. Uma ponta desatualizada continuaria a receber os eventos de dano
 * corretos - o servidor é a autoridade - mas mostraria um acerto sem saber que
 * houve um corte, e o `jewel_proc` que ela não conhece cairia em silêncio.
 *
 * NÃO é um bump de protocolo: `PVP_TEAM_PROTOCOL_VERSION` fica em 4 porque o
 * evento de proc não atravessa o fio neste gate - ele existe na simulação e
 * nenhuma cena o consome.
 *
 * ## 6 → 7: o Orbe Vampírico (gate V28G.I)
 *
 * Um golpe confirmado passa a CURAR quem o deu, numa percentagem do dano
 * realmente aplicado. É uma regra de combate nova, e é a primeira em que o
 * resultado de um golpe muda a vida de um casco que não foi atingido.
 *
 * É um bump de REGRAS porque as duas pontas têm de concordar no que um acerto
 * faz. Uma ponta desatualizada veria a vida do atacante subir sem nada na sua
 * cópia das regras a explicar porquê, e a sua previsão local divergiria a cada
 * tiro em vez de convergir.
 *
 * NÃO é um bump de protocolo nem de bilhete. O perfil de joias já viaja desde o
 * V28G.A e a sua FORMA não muda; a vida de cada casco já viajava nos quadros. E
 * não há evento novo: **este gate não tem VFX**, portanto nada de novo
 * atravessa o fio - `PVP_TEAM_PROTOCOL_VERSION` fica onde está.
 */
/*
 * 7 -> 8: o Núcleo Atômico (gate V28G.J).
 *
 * As duas pontas têm de concordar sobre o que uma salva pode largar, e passaram
 * a discordar: uma salva pode agora conter uma BOMBA, que voa a 80 % da
 * velocidade, deteta por proximidade, explode num raio de três cascos e EMPURRA
 * quem apanha. Um cliente que não soubesse disto desenhava um tiro comum a ir
 * devagar, não desenhava explosão nenhuma, e via a sua própria nave a ser
 * arrastada sem razão.
 *
 * E aqui isto é também um bump de PROTOCOLO, ao contrário do V28G.D e do V28G.F:
 * este gate TEM VFX, e três campos novos atravessam o fio.
 *
 * ## 8 -> 9: o Arsenal Ígneo (gate V28G.L)
 *
 * As duas pontas têm de concordar sobre o que uma salva pode LARGAR AO LADO, e
 * passaram a discordar: uma salva pode agora lançar um MÍSSIL, que não substitui
 * tiro nenhum, persegue um inimigo escolhido pela autoridade, curva a 240 graus
 * por segundo, vive nove segundos e explode ao tocar num inimigo, numa parede ou
 * no limite. Um cliente que não soubesse disto veria vida a desaparecer sem nada
 * na sua cópia das regras a explicar de onde o golpe veio.
 *
 * E é também um bump de PROTOCOLO, pela mesma razão que o V28G.J foi: este gate
 * TEM VFX, e uma lista nova - `mis` - atravessa o fio nos dois protocolos.
 *
 * NÃO é um bump de bilhete: o perfil de joias já viaja desde o V28G.A e a sua
 * FORMA não muda.
 *
 * ## 9 -> 10: a Égide Espinhosa (gate V28G.O)
 *
 * As duas pontas têm de concordar sobre uma coisa que nenhuma versão anterior
 * podia acontecer: **um efeito hostil pode mudar de dono a meio**. Um tiro que
 * ia acertar no defensor passa a ferir o atacante; um míssil desaparece de um
 * lado da arena e nasce no outro, agora perseguindo quem o lançou; uma marca de
 * veneno aparece em quem a mandou.
 *
 * Um cliente com a versão 9 veria as três coisas como defeitos: dano a aparecer
 * do nada no atacante, um míssil a teleportar-se, e uma marca no casco errado.
 * Não é uma decoração que ele possa ignorar - é a mecânica a fazer o contrário
 * do que a cópia de regras dele diz.
 *
 * Este número cobre TODAS as versões que este gate move, e as duas mais
 * pequenas estão nas suas próprias casas: os dois protocolos - `rfl` atravessa
 * o fio, portanto ambos sobem - e `PVP_SNAPSHOT_V3_VERSION`, que passa a 14 pela
 * linhagem e pelos fluxos defensivos.
 *
 * NÃO é um bump de bilhete: o perfil de joias já viaja desde o V28G.A e a sua
 * FORMA não muda - a Égide Espinhosa é uma entrada como as outras catorze.
 *
 * E NÃO é um bump de `BALANCE_VERSION`: essa versão governa as taxas de ganho da
 * CAÇA, e este gate não toca em `hunt/`. A decisão do dono foi PvP só, e a
 * reflexão na caça continua pendente na D-4.4.
 *
 * ## 10 -> 11: a Égide Esmeralda (gate V28G.Q)
 *
 * As duas pontas têm de concordar sobre **quanto de um golpe chega a acontecer**.
 * Todo o dano hostil endereçado a um casco - tiro, corte, serra, míssil, bomba,
 * cada parcela de veneno, cada detonação de sangramento e o dano refletido -
 * passa a ser multiplicado por `10 000 - 200 x nível` antes do clamp da vida.
 *
 * Um cliente com a versão 10 desenharia a barra certa - o servidor continua a
 * ser a autoridade e o `hit` que ele publica já traz a magnitude aplicada - mas
 * a PREVISÃO local dele estaria errada em todos os golpes, e por muito: ao nível
 * 30 ele esperaria dois golpes e meio de dano por cada um que acontece. Não é
 * uma decoração que ele possa ignorar; é a aritmética do combate a mudar.
 *
 * NÃO é um bump de protocolo: **este gate não tem VFX**, não há evento novo, e
 * nada de novo atravessa o fio - a vida e o `hit` já viajavam e continuam a
 * viajar com a mesma forma. Os dois `PVP_TEAM_PROTOCOL_VERSION` ficam onde
 * estão.
 *
 * NÃO é um bump de `PVP_SNAPSHOT_V3_VERSION`: a redução não guarda estado
 * nenhum. Ela é uma multiplicação sobre um payload em trânsito, o resto
 * sub-milli não é carregado (ver `damage/damage-reduction.ts`), e o carry que
 * existe - `damageCarryMilli` - já estava no formato e não mudou de unidade.
 *
 * NÃO é um bump de bilhete: o perfil de joias viaja desde o V28G.A e a sua FORMA
 * não muda - a Égide Esmeralda é uma entrada como as outras catorze.
 *
 * E é acompanhado por um bump de `BALANCE_VERSION`, ao contrário do V28G.O:
 * esta joia vale nos DOIS modos, e uma nave que recebe menos dano na caça morre
 * menos e rende mais. *
 * ## 11 -> 12: o Coração do Titã, e a GEOMETRIA do casco (gate V28G.R)
 *
 * As duas pontas têm de concordar sobre **que tamanho um casco tem**, e essa é a
 * mudança maior deste bump.
 *
 * A vida máxima de um casco deixa de ser a mesma para toda a gente: ela é
 * composta - toda a vida plana, vezes a soma das percentagens, aplicada uma vez -
 * e o Coração do Titã soma até `200 x 30` bps a essa soma. E o TAMANHO do casco
 * passa a sair dela, pela raiz da vida máxima final, sem tecto.
 *
 * Um cliente com a versão 11 desenharia todas as naves do tamanho da arte e
 * preveria o seu próprio movimento com o raio da nave de referência - portanto
 * encostaria a nave a uma parede num sítio onde a sala não a encosta, e seria
 * corrigido a cada tique. A hitbox, o ponto onde um tiro nasce, a órbita das
 * serras, a caixa do míssil e a proximidade da bomba mudam todas com o casco:
 * não é decoração que ele possa ignorar, é a geometria do combate.
 *
 * NÃO é um bump de protocolo. **Este gate não tem VFX** e nada de novo atravessa
 * o fio: a vida máxima já viajava POR PARTICIPANTE em `combatProfile`, desde a
 * versão 3 do protocolo do duelo e desde sempre no `roster` do 3x3, e é dela que
 * as duas pontas derivam a escala pela mesma função. `arena.maxHealth` continua
 * a ser a referência normalizada por que a curva divide, exatamente como o
 * `wire.ts` já dizia. Os dois `*_PROTOCOL_VERSION` ficam onde estão.
 *
 * NÃO é um bump de `PVP_SNAPSHOT_V3_VERSION`: a composição é escrita em
 * `profile.maxHealth`, um campo que o formato já persiste e já valida a vida
 * contra - o `snapshot-v3.ts` diz desde a versão 5 que ele existe precisamente
 * para poder carregar "a hull carrying a maximum-health jewel". A geometria é
 * DERIVADA dele a cada leitura e não guarda estado nenhum.
 *
 * NÃO é um bump de bilhete: o perfil de joias viaja desde o V28G.A e a sua FORMA
 * não muda - o Coração do Titã é uma entrada como as outras catorze.
 *
 * E é acompanhado por um bump de `BALANCE_VERSION`, como a Égide Esmeralda: esta
 * joia vale nos DOIS modos, e uma nave com mais vida na caça morre menos e rende
 * mais.
 */
/*
 * 12 -> 13: uma vida máxima só, para a caça e para o duelo (gate V28G.S).
 *
 * O duelo deixou de normalizar a vida em `PVP_MAX_HEALTH`. `profile.maxHealth`
 * é agora a vida máxima AUTORITATIVA da conta - a mesma que a caça usa, ao
 * ponto de vida - resolvida ao emitir o bilhete e transportada assinada. Duas
 * pessoas na mesma sala entram com dois cascos diferentes porque têm duas
 * contas diferentes, e a métrica do casco cresce com esse número em todo o
 * domínio: 100 é `0,1x`, 1 000 é `1x`, 16 000 é `16x`.
 *
 * É um bump de REGRAS e não de protocolo: nada de novo atravessa o fio - a
 * vida máxima já viajava por participante em `combatProfile` - mas o que aquele
 * campo SIGNIFICA mudou, e um cliente ou uma sala de uma build anterior leriam
 * um casco que não é o daquela pessoa. Um bilhete emitido sob a versão errada é
 * recusado em vez de tolerado, como sempre foi.
 *
 * Acompanhado por `PVP_TICKET_VERSION` 6 -> 7 e `PVP_TEAM_TICKET_VERSION`
 * 7 -> 8: as duas capacidades ganharam um campo assinado.
 *
 * NÃO é um bump de `PVP_SNAPSHOT_V3_VERSION`: a vida máxima já é persistida por
 * participante em `profile.maxHealth` desde a versão 5 do documento, e é já
 * contra ela que a vida é validada. A FORMA não mudou - mudou a fonte do número
 * que lá é escrito, e um documento antigo continua a descrever exatamente a
 * partida que foi jogada.
 *
 * NÃO é um bump de `BALANCE_VERSION`: a caça compõe a vida máxima exatamente
 * como compunha, pelas mesmas parcelas e pela mesma função. A mesma impressão
 * digital continua a descrever a mesma vida máxima e a mesma taxa de ganho.
 *
 * NÃO é um bump de `JEWEL_CONTENT_VERSION`: nenhum coeficiente se moveu.
 */
/*
 * 13 -> 14: a vida maxima deixa de ser truncada (gate V28G.S.1).
 *
 * O V28G.S deu ao duelo a vida maxima da conta e converteu-a para pontos
 * inteiros ao emitir o bilhete. A auditoria mediu o custo dessa conversao: o
 * `flatMaxHealth` de um componente e sorteado por MILLI e o `maxHealthPercent`
 * por BPS, portanto um casco de 102,999 entrava no duelo com 102.
 *
 * Agora a unidade e o milesimo, de ponta a ponta - resolvedor, bilhete, perfil,
 * simulacao, instantanea, quadro e barra - e a caca e o duelo tem exatamente a
 * mesma vida, fracao incluida.
 *
 * Acompanhado por `PVP_TICKET_VERSION` 7 -> 8, `PVP_TEAM_TICKET_VERSION` 8 -> 9,
 * os dois `*_PROTOCOL_VERSION` e `PVP_SNAPSHOT_V3_VERSION` 15 -> 16.
 *
 * `BALANCE_VERSION` e `JEWEL_CONTENT_VERSION` NAO se movem: a caca compoe a
 * mesma vida que compunha - ela ja era fracionaria - e nenhum coeficiente mudou.
 */
/*
 * 14 -> 15: o Passo Alado move a nave, e a REGRA de movimento muda (gate V28G.T).
 *
 * A velocidade de um casco deixou de ser `profile.speedBps` e passou a ser
 * `profile.speedBps` mais a percentagem de movimento das joias congeladas,
 * somada uma vez e só depois multiplicada pela runa. É a mesma composição na
 * sala e na previsão do cliente, pela mesma função.
 *
 * É um bump de REGRAS e não de protocolo: nada de novo atravessa o fio - o perfil
 * de joias viaja por participante desde o V28G.A - mas o deslocamento que aquele
 * perfil PRODUZ mudou, e uma sala ou um cliente de uma build anterior integrariam
 * um passo que não é o desta. Um bilhete emitido sob a versão errada é recusado
 * em vez de tolerado, como sempre foi.
 *
 * NAO é um bump dos dois `*_PROTOCOL_VERSION`: nenhum quadro ganhou, perdeu ou
 * mudou um campo. A velocidade efetiva nunca viajou no fio - ela é DERIVADA do
 * perfil, dos dois lados, e continua a ser.
 *
 * NAO é um bump de `PVP_TICKET_VERSION` nem de `PVP_TEAM_TICKET_VERSION`: o
 * bilhete transporta o perfil de joias tal como transportava, e o Passo Alado é
 * uma entrada como as outras catorze. Nenhuma capacidade nova é assinada.
 *
 * NAO é um bump de `PVP_SNAPSHOT_V3_VERSION`: o documento persiste
 * `profile.speedBps` e `profile.jewels` desde antes deste gate, e é deles que a
 * velocidade sai a cada leitura. A FORMA não mudou, e um documento antigo
 * continua a descrever exatamente a partida que foi jogada.
 *
 * NAO é um bump de `JEWEL_CONTENT_VERSION`: nenhuma migração de conteúdo
 * acompanha isto. Um coeficiente novo no catálogo não é uma projeção nova na
 * base, e subir aquele número sem escrever a migração deixaria o código e o banco
 * a discordarem.
 *
 * É acompanhado por `BALANCE_VERSION` 19 -> 20, porque a joia vale nos DOIS
 * modos e a nave da caça passa a vaguear mais depressa.
 */
/*
 * 15 -> 16: o Coração Vital cura, e uma salva passa a poder devolver vida
 * (gate V28G.U).
 *
 * Uma salva comum ganhou uma segunda coisa que pode produzir: 6 % de chance, ao
 * nível 30, de curar 150 % do dano-base ao aliado ferido mais próximo - ou a si
 * própria, quando não há nenhum. É uma rolagem NOVA, de um fluxo novo, feita em
 * cada salva com joia ou sem ela.
 *
 * É um bump de REGRAS e não de protocolo: nada de novo atravessa o fio - o
 * anúncio viaja pelo canal de proc que já carrega `e`, `p`, `q`, `h`, `i` e `t`
 * desde o V28G.I - mas o que aquele perfil PRODUZ mudou, e uma sala de uma build
 * anterior não consumiria a rolagem que esta consome. Duas salas com a mesma
 * semente deixariam de ser a mesma sala, e é exatamente isso que esta versão
 * existe para impedir.
 *
 * NAO é um bump dos dois `*_PROTOCOL_VERSION`: nenhum quadro ganhou, perdeu ou
 * mudou um campo. `pvpProcFrameSchema` já tem o autor, o alvo, a magnitude de
 * cura, a salva e o tique.
 *
 * NAO é um bump de `PVP_TICKET_VERSION` nem de `PVP_TEAM_TICKET_VERSION`: o
 * bilhete transporta o perfil de joias tal como transportava, e o Coração Vital é
 * uma entrada como as outras catorze. Nenhuma capacidade nova é assinada.
 *
 * É acompanhado por `PVP_SNAPSHOT_V3_VERSION` 16 -> 17, porque há um fluxo novo
 * a persistir - sem ele, a salva seguinte a uma reconexão repetiria uma rolagem
 * que a partida já tinha gasto - e por `BALANCE_VERSION` 20 -> 21, porque a joia
 * vale nos DOIS modos e a nave da caça passa a recuperar vida.
 *
 * NAO é um bump de `JEWEL_CONTENT_VERSION`: nenhuma migração de conteúdo
 * acompanha isto.
 */
/*
 * 16 -> 17: o casco mede-se contra a VIDA-BASE, e uma conta nova volta a medir
 * a sua arte (gate V28G.V).
 *
 * A métrica do casco - `escala = vidaMaxima / referencia` - dividia por 1 000,
 * herdado de quando o duelo normalizava o equipamento fora. O V28G.S ligou a
 * vida máxima AUTORITATIVA da conta ao duelo e não moveu esse denominador, de
 * forma que uma conta nova, com 100 de vida, era desenhada e COLIDIA a 0,1x:
 * quatro píxeis de casco, com a barra de vida a flutuar sobre nada.
 *
 * A referência passa a ser a vida-base da nave, que é neutra e vive em
 * `hull/base-health.ts` - a mesma de que a composição de vida máxima parte.
 * Não há um segundo 100 escrito dentro do PvP, e `pvp/` não importa `hunt/`.
 *
 * A curva continua linear, sem teto e sem piso: 100 -> 1x, 101 -> 1,01x,
 * 160 -> 1,6x, 1 000 -> 10x, 10 000 -> 100x. Cada ponto de vida acrescenta
 * exatamente 0,01x, que é o que faz um ponto comprado ver-se.
 *
 * É um bump de REGRAS, e é obrigatório: o raio do casco é a hitbox, portanto a
 * COLISÃO observável mudou. Uma sala ou um cliente de uma build anterior
 * prediriam um corpo de outro tamanho, e um bilhete emitido sob a versão errada
 * é recusado em vez de tolerado.
 *
 * NAO é um bump dos dois `*_PROTOCOL_VERSION`: nenhum quadro ganhou, perdeu ou
 * mudou um campo. A escala nunca viajou no fio - ela é DERIVADA da vida máxima,
 * dos dois lados, e continua a ser.
 *
 * NAO é um bump de `PVP_SNAPSHOT_V3_VERSION`: a FORMA não mudou. Um documento
 * antigo guarda vidas máximas que continuam a ser vidas máximas válidas; o que
 * mudou é o tamanho que elas desenham, e isso é uma regra e não um formato.
 *
 * NAO é um bump de `BALANCE_VERSION`: a caça não se move. A vida-base continua
 * a valer 100 e a nave da caça continua a medir 96 unidades fixas - o que mudou
 * foi de onde o 100 é lido.
 *
 * ## 18: a ARENA cresce, os obstáculos saem, e as naves bloqueiam (gate V28H.K)
 *
 * O contrato do catálogo (gate V28H.B) registou isto antes do Catolé e do
 * Leviatã, e são três coisas de uma vez:
 *
 * - a caixa passa a **24 000 x 15 000** - cinco vezes por cinco vezes, 25x a
 *   área. O Catolé leva literalmente 6x o raio da Bomba Atômica e não cabia na
 *   caixa antiga;
 * - `PVP_OBSTACLES` fica **vazia**. Os cinco blocos saíram;
 * - **um casco deixa de atravessar outro casco.** Uma nave é expulsa de outra
 *   como era expulsa de um pilar, e como o raio sai da vida máxima, uma nave
 *   tanque É a parede - que é o papel que o contrato lhe deu.
 *
 * É um bump de REGRAS, e é obrigatório pelo terceiro ponto: o cliente PREDIZ o
 * seu próprio movimento, e uma build que não saiba que outro casco a trava
 * diverge da sala ao primeiro encontrão e corrige para sempre. Um bilhete
 * emitido sob a versão errada é recusado em vez de tolerado.
 *
 * NAO é um bump dos dois `*_PROTOCOL_VERSION`: nenhum quadro ganhou, perdeu ou
 * mudou um campo. As dimensões e a lista de estruturas sempre viajaram no
 * `welcome`, e continuam a viajar - o que mudou foram os valores.
 *
 * NAO é um bump de `PVP_SNAPSHOT_V3_VERSION`: a FORMA não mudou. Um documento
 * antigo guarda coordenadas que continuam a ser coordenadas válidas, e a caixa
 * nova contém a antiga inteira.
 *
 * NAO é um bump de `BALANCE_VERSION`: a caça não tem arena nenhuma.
 */
/*
 * 18 -> 19: a Lança Solar cresceu, e a carga passou a soltar-se (V28H.N, V28H.P).
 *
 * Duas mudanças de REGRAS, e as duas obrigam as duas pontas a concordar:
 *
 * - a carga da Lança Solar é largada por uma ORDEM e já não por um relógio, e
 *   o que ela produz - espessura e dano - cresce com o tempo aguentado. Uma
 *   ponta antiga não teria como soltar nada, e uma carta que carrega e nunca
 *   dispara não é uma carta;
 * - o alcance e a espessura do feixe mudaram de valor, e mudaram de CASA: eles
 *   eram uma proporção do casco partilhada pelos dois modos e passaram a ser
 *   medidas de arena, porque a nave do duelo ocupa 2 % do ecrã e a da caça
 *   13 % - o mesmo múltiplo dava um feixe 3,7 vezes mais curto num modo do que
 *   no outro. Uma ponta antiga desenharia o feixe fino onde a sala cobra o
 *   grosso, que é a definição de um cliente a mentir sobre o combate.
 *
 * É acompanhado por um bump de `BALANCE_VERSION`, ao contrário de quase todos os
 * anteriores: esta carta vale nos DOIS modos, e um feixe maior mata mais na
 * caça.
 */
/*
 * 19 -> 20: o arrefecimento da Esfera Ígnea conta desde o DISPARO (V28I.C).
 *
 * Uma mudança de regra, e das que as duas pontas têm de partilhar: a carta
 * carrega até quinze segundos e arrefece 8,8 s ao nível 6, portanto contar desde
 * a ordem punha-a pronta outra vez seis segundos ANTES de sequer disparar - o
 * arrefecimento deixava de ser um preço. Agora ele começa no instante em que a
 * esfera sai.
 *
 * Uma ponta antiga desenharia a barra da carta a ficar verde a meio da carga, e
 * ofereceria uma activação que a sala recusa: a barra do HUD lê o mesmo número
 * que a sala aplica, e é por isso que isto é uma regra e não uma decoração.
 *
 * **Não é acompanhado por um bump de protocolo.** Nenhum campo mudou de forma:
 * `ck` já levava o arrefecimento restante de cada espaço, e o que mudou foi
 * QUANDO ele passa a correr. Um frame da versão 16 continua a ser lido por um
 * cliente da versão 16 sem ambiguidade nenhuma.
 */
/*
 * 20 -> 21: uma ordem OFENSIVA aceite encerra o Manto Espectral (V28H.V).
 *
 * Ele encerrava só no tiro de canhão, e isso deixava um buraco: um jogador
 * encoberto podia ligar o Turbilhão, o Catolé ou a Lança e continuar invisível a
 * ferir. A decisão do dono fecha-o - "qualquer ataque ou activação de carta
 * ofensiva encerra imediatamente" - e fecha-o no instante em que a ORDEM é
 * aceite, e não quando o dano acerta: a Lança e a Esfera revelam quem as usa no
 * início da carga, que é quando o adversário passa a ter direito a ver o que vem
 * aí.
 *
 * É uma regra e não uma decoração, e por isso obriga as duas pontas a
 * concordar: a barra do HUD lê o mesmo temporizador que a sala apaga, e uma
 * ponta antiga desenharia "ATIVA" durante nove segundos sobre uma nave que já
 * toda a gente vê.
 *
 * **Sem bump de protocolo**, e é deliberado: nenhum campo mudou de forma. O que
 * mudou foi QUANDO a ranhura do Manto vai a zero - e ela já viajava, na mesma
 * lista de sempre. Um frame da versão 17 continua a ser lido por um cliente da
 * versão 17 sem ambiguidade nenhuma.
 */
/*
 * 21 -> 22: o gelo passou a VOAR, e a sala passou a sortear (gate V28H.W).
 *
 * Quatro mudanças de regra, e todas obrigam as duas pontas a concordar:
 *
 * - o Congelamento e a Lentidão deixaram de acontecer no instante da ordem e
 *   passaram a lançar um corpo teleguiado que pode falhar;
 * - a duração das duas passou a ser sorteada pela sala - dois a quatro segundos
 *   e três a seis - a partir do id do projéctil, portanto igual na re-simulação;
 * - o Congelamento trava o ataque em 30% das vezes em vez de sempre, e a
 *   resposta viaja na magnitude da ranhura;
 * - o Malware reduz metade da cura, e não sessenta por cento.
 *
 * Uma ponta antiga aplicaria os efeitos no instante da ordem e travaria sempre o
 * ataque, que é um combate diferente do que a sala está a arbitrar.
 */
/*
 * 22 -> 23: o raio passou a VOAR, e o Leviatã passou a ser pedido (V28H.X).
 *
 * Cinco mudanças de regra, e todas obrigam as duas pontas a concordar:
 *
 * - o **Curto-Circuito** deixou de acontecer no instante da ordem e passou a
 *   lançar um corpo teleguiado que pode falhar. A descrição aprovada sempre
 *   disse "dispara energia elétrica", e a implementação tocava;
 * - a **Marca da Caveira** continua a tocar, mas alcança agora a distância a que
 *   um duelo abre. Com os trinta raios de casco que tinha, ela era recusada em
 *   silêncio no princípio de todas as partidas;
 * - **entrar no Leviatã deixou de ser automático**: é preciso pedir. Uma
 *   estrutura em que se entrava por encostar era uma armadilha - quem passasse
 *   ao lado ficava lá dentro treze segundos sem ter pedido nada - e a caixa de
 *   entrada cresceu de 1,2 para 2 raios de casco;
 * - os **seis canhões** dele disparam oito salvas por segundo, ou à cadência do
 *   piloto se ela for mais rápida - com as joias e o Curto-Circuito dele
 *   incluídos - e cada tiro nasce numa boca diferente;
 * - e o **raio do Curto-Circuito passou a acertar**: a pergunta "este corpo
 *   rebenta ou aplica um efeito?" era uma lista escrita à mão que não o
 *   incluía, portanto ele tocava no casco e não fazia nada. Agora é uma função
 *   exaustiva, e um corpo novo sem decisão não compila.
 *
 * Uma ponta antiga não sabe pedir para pilotar, aplicaria o curto-circuito no
 * instante da ordem, e desenharia uma cadência que a sala não está a arbitrar.
 */
export const PVP_RULES_VERSION = 23;
